import { Fragment } from 'preact';
import { ErrorMessage } from '@/features/elements';
import { honeyBotId, focusableBlocks } from '@/constants';
import { makeClassName } from '@/utils';
import { useConfigStore, useFormProps } from '../stores/ConfigStore';
import { useFormStore } from '../stores/FormStore';
import useBlock from '../hooks/useBlock';
import usePages from '../hooks/usePages';
import useValidatedBlocks from '../hooks/useValidatedBlocks';
import SvgIcon from './SvgIcon';
import FormBlock from './FormBlock';
import ActionButton from './ActionButton';

export default function Page({ pageNumber }) {
	const { formKey } = useFormProps();
	const {
		activePage,
		loading,
		postFeedbackError,
		showErrors,
		showNavAutopostPages,
		actions,
		actionButtonRef,
		firstFocusableBlockRef
	} = useFormStore();

	const {
		text,
		pageMap,
		properties: { advanced },
		blocks
	} = useConfigStore();
	const pageLayout = usePages(pageNumber);
	const { pageValid, firstInvalid } = useValidatedBlocks(pageLayout, showErrors);
	const { block = {} } = useBlock({ typeName: 'page_break', typeOf: pageNumber - 1 });
	const nextLabel = block.properties?.nextLabel ?? block?.nextLabel ?? 'Next';
	const prevLabel =
		block.properties?.prevLabel ?? block?.prevLabel ?? text.btnLastBackText ?? 'Back';

	const isActive = activePage === Number(pageNumber);
	const pageCount = Number(Object.keys(pageMap).pop());
	const isFirstPage = pageNumber === 1;
	const isLastPage = pageCount === pageNumber;

	const showPrev = !isFirstPage && !advanced.hideSubNavigation;
	const showNext =
		!isLastPage &&
		pageCount > 1 &&
		!advanced.hideSubNavigation &&
		(!block.properties?.autopost || showNavAutopostPages.indexOf(activePage) > -1);
	const showSubmit =
		isLastPage &&
		!advanced.hideNavigation &&
		(!advanced.lastPageAutoPost || showNavAutopostPages.indexOf(activePage) > -1);

	const firstFocusableBlockId = isActive
		? pageLayout.find(id => {
				const block = Object.values(blocks ?? {}).find(
					block => Number(block.id) === id && block?.properties?.hide_on_init !== true
				);
				if (!block) {
					return null;
				}
				return focusableBlocks.indexOf(block.typeName) > -1;
		  })
		: null;

	//force refresh button ref to current page
	const focusableNavigation = isActive && !firstFocusableBlockId;

	return (
		<div
			id={`page${pageNumber}-${formKey}`}
			style={{ display: isActive ? 'block' : 'none' }}
			data-testid={`page-${pageNumber}`}
		>
			<FormBlock id={honeyBotId} />
			{pageLayout.map(id => (
				<div
					ref={isActive && firstFocusableBlockId === id ? firstFocusableBlockRef : null}
				>
					<FormBlock
						id={id}
						showErrors={showErrors}
						firstInvalid={isActive && firstInvalid && id === firstInvalid}
					/>
				</div>
			))}
			<div
				className={makeClassName(
					{ prefix: false, className: 'form-actions' },
					!showPrev && 'row-reverse'
				)}
				data-testid="page_break-group"
				ref={isActive && focusableNavigation ? firstFocusableBlockRef : null}
				aria-live="polite"
			>
				<Fragment>
					{showPrev && (
						<ActionButton
							type="prev"
							id={block.id ?? formKey}
							onClick={() => actions.prevPage()}
						>
							<SvgIcon
								icon="angle-left"
								className="is-context-icon"
							/>{' '}
							{prevLabel}
						</ActionButton>
					)}

					{showNext && (
						<ActionButton
							type="next"
							id={block.id}
							ref={isActive ? actionButtonRef : null}
							loading={loading}
							onClick={() => {
								if (!pageValid) {
									actions.dispatch({
										type: 'set_show_errors',
										payload: {
											pageLayout
										}
									});
									return;
								}
								actions.nextPage();
							}}
						>
							{nextLabel}{' '}
							<SvgIcon
								icon="angle-right"
								className="is-context-icon"
							/>
						</ActionButton>
					)}

					{showSubmit && (
						<ActionButton
							type="submit"
							ref={isActive ? actionButtonRef : null}
							loading={loading}
							onClick={() => {
								if (!pageValid) {
									actions.dispatch({
										type: 'set_show_errors',
										payload: {
											pageLayout
										}
									});
									return;
								}
								actions.postFeedback();
							}}
						>
							<div>
								{text.btnSubmitText}{' '}
								<SvgIcon
									icon="paper-plane-o"
									className="is-context-icon"
								/>
							</div>
						</ActionButton>
					)}
					{postFeedbackError && (
						<ErrorMessage showErrors>Something went wrong, please try again</ErrorMessage>
					)}
				</Fragment>
			</div>
		</div>
	);
}
